import React from "react";
import { AppliedCouponType, useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { CreditCard, SealPercent, Truck, X } from "@phosphor-icons/react";
import { useLocale } from "lib/hooks/useLocale";
import { CouponType } from "lib/types/checkout";
import { Icon } from "@shopflo/ui/icon";
import { currencyFormatter } from "lib/utils/formatters";
import { DiscountConfigType } from "lib/types/merchantConfigTypes";

interface AppliedItemProps {
  coupon: AppliedCouponType;
  handleDeleteItem: (value: string) => void;
  customisations?: DiscountConfigType;
}

const AppliedItem: React.FC<AppliedItemProps> = ({ coupon, handleDeleteItem, customisations }) => {
  const { t } = useLocale();
  const {
    state: { shippingHandles },
    actions: { setDiscountProductSelectorModal },
  } = useCheckoutContext();

  const selectedShippingHandle = shippingHandles?.find((handle) => handle?.selected_handle);

  const renderDiscountedValue = () => {
    if (!coupon?.discountValue) {
      if (coupon?.couponType === CouponType.PAYMENT_OFFER) {
        return t("bank_offer_applied");
      }
      if (coupon?.couponType === CouponType.SHIPPING) {
        const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
        const discountPrice = parseFloat(selectedShippingHandle?.price || "0");

        const discount = originalPrice - discountPrice;
        return `- ${currencyFormatter(discount)}`;
      }
      return t("automatic_freebies_free_text");
    } else {
      return `- ${currencyFormatter(coupon?.discountValue)}`;
    }
  };

  const getIcon = () => {
    if (coupon?.couponType === CouponType.PAYMENT_OFFER) {
      return CreditCard;
    }
    if (coupon?.couponType === CouponType.SHIPPING) {
      return Truck;
    }
    return SealPercent;
  };

  return (
    <div className="flex w-full flex-col cursor-auto py-1.5 items-center">
      <div className="border-box flex w-full flex-row items-center">
        <div className="flex w-full flex-row items-center justify-between overflow-hidden pr-2">
          <h3 className="flex w-full items-center truncate pr-2 text-sm font-normal text-coal-light gap-1.5">
            <Icon
              IconComponent={getIcon()}
              size="xs"
              tabIndex={-1}
              className="text-zinc-500/80 cursor-pointer "
              iconProps={{ weight: "regular", color: customisations?.totalSavings?.infoColor, opacity: 0.7 }}
            />
            <p className="text-xs truncate font-medium uppercase self-end text-zinc-700/70"
              style={{
                color: customisations?.totalSavings?.infoColor,
                opacity: 0.7
              }}
            >{`${Boolean(coupon?.title) ? coupon?.title : coupon?.code
              }`}</p>
          </h3>
          {!Boolean(coupon?.isProductSelector) ? (
            <p className="inline-flex w-1/2 justify-end text-sm font-medium text-yay-dark">
              <span className="text-xs font-medium text-zinc-700/70"
                style={{
                  color: customisations?.totalSavings?.infoColor,
                  opacity: 0.7
                }}
              >{renderDiscountedValue()}</span>
            </p>
          ) : (
            <p
              className="inline-flex w-1/2 space-x-1 justify-end overflow-hidden text-sm font-medium text-yay-dark underline cursor-pointer"
              onClick={() => setDiscountProductSelectorModal("DISCOUNTED_PRODUCT_SELECTOR")}
              style={{
                color: customisations?.totalSavings?.infoColor,
                opacity: 0.7
              }}
            >
              {t("view")}
            </p>
          )}
        </div>

        {/* Disable delete coupon button if it is auto applied */}
        {Boolean(coupon?.code) && Boolean(coupon?.isRemovable) && !Boolean(coupon?.isProductSelector) && (
          <span onClick={() => handleDeleteItem(coupon?.code)}>
            <div className="flex h-3.5 w-3.5 items-center justify-center">
              <Icon
                IconComponent={X}
                size="xxxs"
                tabIndex={-1}
                className="text-zinc-700/70 cursor-pointer "
                iconProps={{ weight: "bold", color: customisations?.totalSavings?.infoColor, opacity: 0.7 }}
              />
            </div>
          </span>
        )}
      </div>
    </div>
  );
};

export default AppliedItem;
